import Layout from "components/wrappers/Layout";
import Theme from "styles/theme";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import { ConfigProvider, theme as antdTheme } from "antd";
import ScrollToTop from "components/assets/utilities/ScrollToTop";
import { AdminViewModal } from "components/assets/utilities/AdminViewModal";
import SessionProvider from "components/wrappers/SessionProvider";
import RunPageSelector from "components/wrappers/RunPageSelector";
import { useEffect } from "react";
import { useAppSelector, useDarkMode, useThemeColors, useTheme } from "redux/hooks";
import Home from "./components/pages/Home";
import ModelPage from "./components/pages/Model";
import QACheckPage from "./components/pages/QA";
import ResultPage from "./components/pages/Result";
import JobPage from "./components/pages/Job";
import ModelFlowPage from "components/pages/Flow";
import IOPage from "components/pages/IO";
import FeedbackPage from "components/pages/Feedback";
import ChatPage from "components/pages/Chat";

function RedirectToDefaultModel() {
    const defaultModel = useAppSelector(state => state.user.settings?.default_model);

    useEffect(() => {
        window.location.replace(`/${encodeURIComponent(defaultModel)}`);
    }, [defaultModel]);
    return null;
}

function App() {
    const isAdmin = useAppSelector(state => state.user.settings?.is_admin ?? false);
    const darkMode = useDarkMode();
    const themeColors = useThemeColors();
    const themeName = useTheme();

    useEffect(() => {
        document.body.style.backgroundColor = themeColors.backgroundColor;
        document.body.style.color = themeColors.textColor;
        const root = document.documentElement;
        root.style.setProperty("--bg-color", themeColors.backgroundColor);
        root.style.setProperty("--bg-color-secondary", themeColors.backgroundColorSecondary);
        root.style.setProperty("--bg-color-tertiary", themeColors.backgroundColorTertiary);
        root.style.setProperty("--text-color", themeColors.textColor);
        root.style.setProperty("--text-color-secondary", themeColors.textColorSecondary);
        root.style.setProperty("--text-color-muted", themeColors.textColorMuted);
        root.style.setProperty("--border-color", themeColors.borderColor);
        root.style.setProperty("--border-color-secondary", themeColors.borderColorSecondary);
        root.style.setProperty("--primary-color", themeColors.primaryColor);
        root.style.setProperty("--primary-color-hover", themeColors.primaryColorHover);
        root.style.setProperty("--primary-color-light", themeColors.primaryColorLight);
        root.style.setProperty("--hover-bg", themeColors.hoverBackground);
        root.style.setProperty("--selected-bg", themeColors.selectedBackground);
        // Cache the resolved theme palette so the next page load can paint
        // the loading screen / body before redux is hydrated. Stored as the
        // full themeColors object (palettes can change shape over time, so we
        // serialize the whole thing rather than just the theme name) plus the
        // dark-mode flag for antd's algorithm choice.
        try {
            localStorage.setItem("velocity:lastThemeColors", JSON.stringify(themeColors));
            localStorage.setItem("velocity:lastDarkMode", darkMode ? "1" : "0");
            // Cache the theme name too so user.slice can preseed
            // settings.theme on next boot, getting antd's algorithm and
            // useDarkMode/useThemeColors right on the very first render.
            if (themeName) localStorage.setItem("velocity:lastThemeName", themeName);
        } catch {
            // localStorage can throw in private/incognito modes — ignore.
        }
    }, [themeColors, darkMode, themeName]);

    return (
        <ConfigProvider
            theme={{
                algorithm: darkMode ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                token: {
                    colorPrimary: themeColors.primaryColor,
                    colorLink: themeColors.primaryColor,
                    colorLinkHover: themeColors.primaryColorHover,
                    colorBgBase: themeColors.backgroundColor,
                    colorBgContainer: themeColors.backgroundColor,
                    colorBgLayout: themeColors.backgroundColor,
                    colorBgElevated: themeColors.backgroundColorSecondary,
                    colorText: themeColors.textColor,
                    colorTextSecondary: themeColors.textColorSecondary,
                    colorTextTertiary: themeColors.textColorMuted,
                    colorBorder: themeColors.borderColor,
                    colorBorderSecondary: themeColors.borderColorSecondary,
                },
            }}
        >
            <ThemeProvider theme={{ ...Theme, colors: themeColors, darkMode }}>
                <SessionProvider>
                    <>
                    <AdminViewModal />
                    <BrowserRouter>
                        <ScrollToTop />
                        <Layout>
                            <Routes>
                                {!isAdmin && (
                                    <Route path="/" element={<RedirectToDefaultModel />} />
                                )}
                                {isAdmin && (
                                    <>
                                        <Route path="/" element={<Home />} />
                                        <Route path="/chat" element={<ChatPage />} />
                                        <Route path="/:modelName/info" element={<ModelPage />} />
                                        <Route path="/:modelName/qa"  element={<QACheckPage />} />
                                        <Route path="/:modelName/flow" element={<ModelFlowPage />} />
                                        <Route path="/:modelName/io" element={<IOPage />} />
                                        <Route path="/:modelName/feedback" element={<FeedbackPage />} />
                                        <Route path="/:modelName/job/:jobName" element={<JobPage />} />
                                        <Route path="/:modelName/results/:resultName" element={<ResultPage />} />
                                    </>
                                )}
                                <Route path="/:modelName"  element={<RunPageSelector />} />
                            </Routes>
                        </Layout>
                    </BrowserRouter>
                    </>
                </SessionProvider>
            </ThemeProvider>
        </ConfigProvider>
    );
}

export default App;
